/**
 * Page de callback OAuth Google
 * Traite le session_id retourné par Emergent Auth
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Link as LinkIcon, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const hasProcessed = useRef(false);
  
  const [status, setStatus] = useState('processing'); // processing, existing_account, error
  const [existingAccount, setExistingAccount] = useState(null);
  const [googleData, setGoogleData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    // Éviter les doubles appels en StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extraire le session_id du fragment URL
      const hash = location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      
      if (!match) {
        setStatus('error');
        toast.error('Session invalide');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const sid = match[1];
      setSessionId(sid);

      try {
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sid
        });

        const data = response.data;

        if (data.status === 'success') {
          // Connexion réussie
          setAuth(data.access_token, data.user);
          
          if (data.is_new_user) {
            toast.success('Bienvenue sur LEMO ! 🎉');
          } else {
            toast.success('Connexion réussie !');
          }

          // Rediriger selon le rôle
          if (data.user.role === 'Restaurant') {
            navigate('/restaurant-dashboard', { replace: true });
          } else if (data.user.role === 'Admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/restaurants', { replace: true });
          }
        } else if (data.status === 'existing_account') {
          // Un compte existe déjà avec cet email
          setStatus('existing_account');
          setExistingAccount(data.existing_account);
          setGoogleData(data.google_data);
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        toast.error(getApiErrorMessage(error, 'Erreur lors de la connexion'));
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processAuth();
  }, [location.hash, navigate, setAuth]);

  // Lier le compte Google au compte existant
  const handleLinkAccount = async () => {
    setLinking(true);
    try {
      const response = await axios.post(`${API}/auth/google/link-account`, {
        session_id: sessionId,
        link_to_existing: true
      });

      if (response.data.status === 'success') {
        setAuth(response.data.access_token, response.data.user);
        toast.success('Compte Google lié avec succès ! 🔗');
        
        // Rediriger selon le rôle
        if (response.data.user.role === 'Restaurant') {
          navigate('/restaurant-dashboard', { replace: true });
        } else if (response.data.user.role === 'Admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/restaurants', { replace: true });
        }
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la liaison'));
      setLinking(false);
    }
  };

  // Ne pas lier - retourner au login
  const handleUsePassword = () => {
    toast.info('Connectez-vous avec votre email et mot de passe');
    navigate('/login', { replace: true });
  };

  // État: Processing
  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Connexion en cours...</p>
        </div>
      </div>
    );
  }

  // État: Erreur
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Erreur de connexion</p>
          <p className="text-sm text-gray-500 mt-2">Redirection vers la page de connexion...</p>
        </div>
      </div>
    );
  }

  // État: Compte existant - demander à l'utilisateur
  if (status === 'existing_account' && existingAccount) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Compte existant détecté</h1>
            <p className="text-gray-500 mt-2">
              Un compte existe déjà avec l'email <strong>{existingAccount.email}</strong>
            </p>
          </div>

          {/* Infos du compte existant */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Compte actuel :</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 font-semibold">
                  {existingAccount.name?.charAt(0) || existingAccount.email.charAt(0)}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{existingAccount.name || 'Utilisateur'}</p>
                <p className="text-sm text-gray-500">{existingAccount.email}</p>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-6 text-center">
            Voulez-vous lier votre compte Google à ce compte existant ?
          </p>

          <div className="space-y-3">
            <Button
              onClick={handleLinkAccount}
              disabled={linking}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 rounded-full font-semibold"
              data-testid="link-account-button"
            >
              {linking ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <LinkIcon className="w-5 h-5 mr-2" />
              )}
              {linking ? 'Liaison en cours...' : 'Oui, lier mon compte Google'}
            </Button>

            <Button
              onClick={handleUsePassword}
              variant="outline"
              className="w-full h-12 rounded-full font-medium"
              data-testid="use-password-button"
            >
              Non, utiliser mon mot de passe
            </Button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            En liant votre compte, vous pourrez vous connecter avec Google ou votre mot de passe.
          </p>
        </div>
      </div>
    );
  }

  return null;
}
